import "../plan.scss"

import { ProgressBar, RangeSlider, Text } from "@shopify/polaris"
import classNames from "classnames"
import { observer } from "mobx-react"
import { getFocusClassName } from "polaris-enhance"
import { useMemo } from "react"

import { PLAN_LIST_MAP } from "@/constant"
import { PlanButton } from "@/pages/Plan/component"
import stores from "@/stores"

import Styles from "../index.module.scss"
import {
  PaymentChildrenFeatureItem,
  PaymentChildrenPrice,
  PaymentChildrenQuote,
} from "./PaymentChildren"
import PaymentChildrenExtra from "./PaymentChildren"

interface paymentProps {
  type: number
  isSubscribeGuide: boolean
}

const Payment = (props: paymentProps) => {
  const { PlanStore } = stores
  const { config, completedPackageList = [], curPlanStep } = PlanStore
  const { user_pkg } = config || {}

  const { isSubscribeGuide = false, type } = props

  const planListMap = useMemo(() => PLAN_LIST_MAP(), [])

  return (
    <div className="payment-content flex flex-wrap items-stretch xl:flex-nowrap justify-between gap-4">
      {completedPackageList?.map((item: any, index: number) => {
        const current: any =
          item.priceInfo[type][curPlanStep[item.package_name]]

        const info = planListMap[item.kind]
        const isRecommended = user_pkg?.recommend_plan_kind === item.kind
        const discountPercent = current?.discount_rate
          ? Math.round(Number(current.discount_rate) * 100)
          : 0
        const showDiscountBadge =
          discountPercent > 0 &&
          type !== 2 &&
          Number(current?.origin_price) !== Number(current?.actual_price) &&
          current?.id !== user_pkg?.package_id

        return (
          <div
            id={`plan-item-${item.package_name}`}
            className={classNames(
              "plan-item-container flex flex-col self-stretch rounded-2xl",
              getFocusClassName(`plan-item-${item.package_name}`),
              {
                "plan-item-container--recommended": isRecommended,
              },
            )}
            key={item.package_name}
          >
            {isRecommended && <div className="plan-item-container__top-bar" />}
            <div
              className={classNames("flex flex-1 flex-col gap-8", {
                "px-4 pb-4 pt-[10px]": isRecommended,
                "p-4": !isRecommended,
              })}
            >
              <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-between gap-2">
                    <Text variant="headingLg" as="h2">
                      {info?.planText}
                    </Text>
                    {showDiscountBadge && (
                      <span className="inline-flex shrink-0 items-center rounded-lg bg-[#91d0ff] px-2 py-[2px] text-xs font-medium leading-4 text-[#002133]">
                        {`${discountPercent}% OFF`}
                      </span>
                    )}
                  </div>
                  <Text variant="bodyMd" as="p" tone="subdued">
                    {info?.planDesc}
                  </Text>
                </div>

                <div className="flex flex-col gap-4 xl:gap-3">
                  <PaymentChildrenPrice
                    current={current}
                    user_pkg={user_pkg}
                    type={type}
                    package_kind={item.kind}
                  />
                  <div className="flex min-h-[20px] flex-col gap-2 xl:gap-1">
                    {index === 0 ? (
                      <div
                        className={classNames(
                          "flex h-[28px] items-center xl:h-[20px]",
                          Styles.progressBarByHeight,
                        )}
                      >
                        <ProgressBar
                          progress={100}
                          animated={false}
                          size="small"
                          tone="primary"
                        />
                      </div>
                    ) : (
                      <div
                        className={classNames(
                          "h-[28px] xl:h-[20px]",
                          Styles.RangeSliderByHeight,
                        )}
                      >
                        <RangeSlider
                          label=""
                          min={0}
                          step={1}
                          max={item.priceInfo[type]?.length - 1}
                          labelHidden
                          output={false}
                          value={curPlanStep[item.package_name]}
                          onChange={(value) => {
                            PlanStore.setCurPlanStep(
                              item.package_name,
                              Number(value),
                            )
                          }}
                        />
                      </div>
                    )}
                    <div className="pt-1 xl:pt-0">
                      <PaymentChildrenQuote
                        quota={current?.quota}
                        type={type}
                        package_kind={item.kind}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex min-h-[72px] flex-col gap-2">
                  <PlanButton
                    compact
                    cioInfo={{
                      plan_monthly_price: current?.actual_price,
                    }}
                    current={
                      current ?? {
                        id: 1,
                        kind: 1,
                        period: 1,
                        level: 1,
                        origin_price: 0,
                        actual_price: 0,
                        one_price: 0,
                        quota: 6,
                      }
                    }
                    kind={item.kind}
                    isSubscribeGuide={isSubscribeGuide}
                    packageName={item.package_name}
                  />
                  <PaymentChildrenExtra
                    price={current?.one_price}
                    type={type}
                    package_kind={item.kind}
                  />
                </div>
              </div>

              <div className="flex flex-1 flex-col gap-2">
                <Text variant="bodyMd" as="p" fontWeight="medium">
                  {info?.additionalText}
                </Text>
                <div className="flex flex-col gap-2">
                  {info?.list.map((feature) => {
                    if (
                      feature?.id &&
                      feature?.id === "freeReturnServiceWidget" &&
                      type === 2
                    ) {
                      return null
                    }
                    return (
                      <PaymentChildrenFeatureItem
                        key={feature.text}
                        text={feature.text}
                        tooltipContent={feature.tooltipContent}
                        withoutFeature={feature.withoutFeature}
                      />
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default observer(Payment)
